import React, { useState, useEffect } from 'react';
import { api, logHttpError } from '../services/api';
import { useNavigate } from 'react-router-dom';

interface Notification {
  id: number;
  type: string;
  title: string;
  content: string | null;
  from_user_id: number | null;
  from_username: string | null;
  is_read: boolean;
  created_at: string;
}

const NOTIFICATION_ICONS: Record<string, string> = {
  message: '💬',
  like: '❤️',
  comment: '💭',
  wall: '📝',
  award: '',
  ban: '',
};

const NotificationsPage: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    fetchNotifications();
    fetchUnreadCount();
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await api.get('/notifications/');
      setNotifications(res.data);
    } catch (err) {
      logHttpError("Ошибка загрузки уведомлений", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const res = await api.get('/notifications/unread-count');
      setUnreadCount(res.data.count);
    } catch (err) {
      logHttpError("Ошибка загрузки счетчика", err);
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    // Помечаем как прочитанное
    if (!notification.is_read) {
      try {
        await api.put(`/notifications/${notification.id}/read`);
        setNotifications(notifications.map(n =>
          n.id === notification.id ? { ...n, is_read: true } : n
        ));
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (err) {
        logHttpError("Ошибка отметки прочтения", err);
      }
    }

    // Переход в зависимости от типа
    if (notification.type === 'message' && notification.from_user_id) {
      navigate(`/messages`);
    } else if ((notification.type === 'like' || notification.type === 'comment') && notification.from_user_id) {
      navigate(`/profile/${notification.from_user_id}`);
    } else if (notification.type === 'wall' && notification.from_user_id) {
      navigate(`/profile/${notification.from_user_id}`);
    }
  };

  const markAllRead = async () => {
    try {
      await api.put('/notifications/read-all');
      setNotifications(notifications.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      logHttpError("Ошибка отметки всех", err);
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Только что';
    if (minutes < 60) return `${minutes} мин назад`;
    if (hours < 24) return `${hours} ч назад`;
    if (days < 7) return `${days} дн назад`;
    return date.toLocaleDateString('ru-RU');
  };

  return (
    <div className="max-w-3xl mx-auto p-6 pb-24 md:pb-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            🔔 Уведомления
            {unreadCount > 0 && (
              <span className="bg-blue-600 text-white text-sm px-3 py-1 rounded-full">
                {unreadCount} новых
              </span>
            )}
          </h1>
          <p className="text-gray-400 mt-1">Все важные события в одном месте</p>
        </div>
        {unreadCount > 0 && (
          <button
            onClick={markAllRead}
            className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-white text-sm font-bold transition border border-gray-700"
          >
            ✓ Отметить все прочитанными
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full"></div>
        </div>
      ) : notifications.length === 0 ? (
        <div className="text-center py-20 bg-[#1a1d24] rounded-2xl border border-gray-800">
          <p className="text-5xl mb-4">🔔</p>
          <p className="text-gray-400">У вас пока нет уведомлений</p>
          <p className="text-gray-600 text-sm mt-2">Здесь будут появляться лайки, комментарии и сообщения</p>
        </div>
      ) : (
        <div className="space-y-3">
          {notifications.map((notification) => (
            <div
              key={notification.id}
              onClick={() => handleNotificationClick(notification)}
              className={`bg-[#1a1d24] p-4 rounded-2xl border cursor-pointer transition hover:border-gray-600 ${
                notification.is_read 
                  ? 'border-gray-800 opacity-70' 
                  : 'border-blue-500/30 bg-blue-500/5'
              }`}
            >
              <div className="flex items-start gap-4">
                <div className="text-3xl">
                  {NOTIFICATION_ICONS[notification.type] || '🔔'}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className={`font-bold text-sm ${notification.is_read ? 'text-gray-400' : 'text-white'}`}>
                      {notification.title}
                    </h3>
                    {!notification.is_read && (
                      <span className="w-2 h-2 rounded-full bg-blue-500 shrink-0 mt-2"></span>
                    )}
                  </div>
                  {notification.content && (
                    <p className="text-gray-500 text-sm mt-1 truncate">
                      {notification.content}
                    </p>
                  )}
                  <p className="text-xs text-gray-600 mt-2">
                    {formatTime(notification.created_at)}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NotificationsPage;
